import {
  Button,
  ButtonSize,
  ButtonType,
  GhostButton,
  PrimaryButton,
  SecondaryButton,
} from '@ardoq/button';
import { AddIcon } from '@ardoq/icons';
import { FlexBox, Stack } from '@ardoq/layout';
import { Header3, Text } from '@ardoq/typography';
import { ReactNode, RefObject, useState } from 'react';
import {
  getPositionAndMaxHeightVertically,
  ModalBackdrop,
  ModalBox,
  WithDropdown,
} from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { FormSize, FormWrapper, TextInput } from '@ardoq/forms';
import { v4 as uuidv4 } from 'uuid';
import styled, { css } from 'styled-components';
import {
  DashboardFilterPillProps,
  DashboardFilterPillView,
  UnlinkedDashboardFilterPillView,
  UnlinkedDashboardFilterPillProps,
} from '@ardoq/table-filter-bar';
import { type I18nLocale, t, type WithI18nLocale } from '@ardoq/i18n';
import {
  DragAndDropCardWrapper,
  DragAndDropDragState,
  DragAndDropPositioningWrapper,
  DragAndDropSectionsProvider,
  DropPlaceholderBar,
  useDragAndDropContext,
} from '@ardoq/drag-and-drop';
import { colors, s8 } from '@ardoq/design-tokens';
import { CAPTURE_HIDE, CAPTURE_KEEP } from '@ardoq/export/captureMode';
import { DASHBOARD_MAX_WIDTH } from '../consts';
import {
  getValidationErrorMessage,
  validateDashboardFilterName,
} from './validateDashboardFilterName';

const DashboardFilterContainer = styled(FlexBox).attrs({
  justify: 'space-between',
  align: 'center',
  borderRadius: 'medium',
  padding: 'medium',
  width: 'full',
})<{ $maxWidth?: string }>`
  box-sizing: border-box;
  ${({ $maxWidth }) =>
    $maxWidth &&
    css`
      max-width: ${$maxWidth};
    `}
`;

const CreateDashboardFilterButtonAndForm = ({
  createDashboardFilterParameter,
  hasInaccessibleWidgetsForDashboardFilter,
  dashboardFilterNames,
  i18nLocale,
}: {
  createDashboardFilterParameter: (name: string, id: string) => void;
  hasInaccessibleWidgetsForDashboardFilter: boolean;
  dashboardFilterNames: string[];
  i18nLocale: I18nLocale;
}) => {
  const isDisabled = hasInaccessibleWidgetsForDashboardFilter;

  const tooltipText = hasInaccessibleWidgetsForDashboardFilter
    ? t(
        i18nLocale,
        'dashboard:filter.createFilterTooltipNoAccess',
        `You can't setup a dashboard filter, some charts use data sources you don't have access to`
      )
    : undefined;

  const [filterName, setFilterName] = useState('');
  const trimmedFilterName = filterName.trim();
  const validationResult = validateDashboardFilterName(
    trimmedFilterName,
    dashboardFilterNames
  );
  const isValid = validationResult.isValid;
  const errorMessage = getValidationErrorMessage(
    validationResult.error,
    i18nLocale
  );

  return (
    <WithDropdown>
      {(openModal, closeModal, isOpen, getHostBox, hostRef) => {
        const closeAndReset = () => {
          setFilterName('');
          closeModal();
        };

        return (
          <>
            <Button
              isActive={isOpen}
              buttonSize={ButtonSize.SMALL}
              ref={hostRef as RefObject<HTMLButtonElement>}
              onClick={() => {
                openModal();
              }}
              isDisabled={isDisabled}
              data-tooltip-text={tooltipText}
              buttonType={
                dashboardFilterNames.length > 0
                  ? ButtonType.GHOST
                  : ButtonType.TERTIARY
              }
            >
              <AddIcon />
              {t(
                i18nLocale,
                'dashboard:filter.newDashboardFilter',
                'New dashboard filter'
              )}
            </Button>
            {isOpen && (
              <>
                <ModalBackdrop
                  closeModal={closeAndReset}
                  isKeepOpen={true}
                  ignoreKeyboardEvents
                />
                <ModalBox
                  dropdownSize={DropdownSize.AUTO}
                  getHostBox={getHostBox}
                  getPositionAndMaxHeight={(refBox, modalBox) =>
                    getPositionAndMaxHeightVertically({
                      refBox,
                      modalBox,
                      isScrollable: true,
                      forceAlignToRight: false,
                    })
                  }
                >
                  <FormWrapper
                    formSize={FormSize.DEFAULT}
                    gap="medium"
                    padding="medium"
                  >
                    <Stack gap="xsmall">
                      <Text variant="text1Bold">
                        {t(
                          i18nLocale,
                          'dashboard:filter.createDashboardFilterTitle',
                          'Create a dashboard filter'
                        )}
                      </Text>
                      <Text variant="text2" color="textModerate">
                        {t(
                          i18nLocale,
                          'dashboard:filter.createDashboardFilterSubtitle',
                          'Control multiple charts with a single filter'
                        )}
                      </Text>
                    </Stack>

                    <TextInput
                      helperText={t(
                        i18nLocale,
                        'dashboard:filter.filterNameHelperText',
                        'Give your filter a clear, descriptive label'
                      )}
                      label={t(
                        i18nLocale,
                        'dashboard:filter.filterNameLabel',
                        'Filter name'
                      )}
                      onKeyDown={event => {
                        if (
                          event.key === 'Enter' &&
                          trimmedFilterName.length > 0 &&
                          isValid
                        ) {
                          closeAndReset();
                          createDashboardFilterParameter(
                            trimmedFilterName,
                            uuidv4()
                          ); // Generate temp uuidv4 ID here before saving, after saving it will be an ArdoqId
                          event.preventDefault();
                          event.stopPropagation();
                        }
                      }}
                      placeholder={t(
                        i18nLocale,
                        'dashboard:filter.filterNamePlaceholder',
                        'Name'
                      )}
                      onValueChange={setFilterName}
                      value={filterName}
                      autoFocus
                      hasError={!isValid}
                      errorMessage={errorMessage}
                    />
                    <FlexBox justify="flex-end" gap="small">
                      <SecondaryButton onClick={closeAndReset}>
                        {t(i18nLocale, 'dashboard:filter.cancel', 'Cancel')}
                      </SecondaryButton>
                      <PrimaryButton
                        onClick={() => {
                          closeAndReset();
                          createDashboardFilterParameter(
                            trimmedFilterName,
                            uuidv4()
                          ); // Generate temp uuidv4 ID here before saving, after saving it will be an ArdoqId
                        }}
                        isDisabled={trimmedFilterName.length === 0 || !isValid}
                      >
                        {t(i18nLocale, 'dashboard:filter.create', 'Create')}
                      </PrimaryButton>
                    </FlexBox>
                  </FormWrapper>
                </ModalBox>
              </>
            )}
          </>
        );
      }}
    </WithDropdown>
  );
};

export type DashboardFilterReaderIslandProps = {
  dashboardFilterProps: DashboardFilterPillProps[];
  showClearAllButton: boolean;
  clearAllSelectedValues: VoidFunction;
};

export type DashboardFilterReaderIslandRenderProps =
  | { show: false }
  | ({ show: true } & DashboardFilterReaderIslandProps);

export const DashboardFilterReaderIsland = ({
  dashboardFilterProps,
  showClearAllButton,
  clearAllSelectedValues,
  i18nLocale,
}: DashboardFilterReaderIslandProps & WithI18nLocale) => (
  <FlexBox
    width="full"
    align="center"
    gap="small"
    flexWrap
    maxWidth={`${DASHBOARD_MAX_WIDTH}px`}
    data-capture={CAPTURE_KEEP}
  >
    {dashboardFilterProps.map(props => (
      <DashboardFilterPillView key={props.label.label} {...props} />
    ))}
    {showClearAllButton && (
      <GhostButton onClick={clearAllSelectedValues} data-capture={CAPTURE_HIDE}>
        {t(i18nLocale, 'dashboard:filter.clearAll', 'Clear all')}
      </GhostButton>
    )}
  </FlexBox>
);

export type DashboardFilterLinkModeIslandProps = {
  linkModeDashboardFilterParameterName: string;
  exitLinkMode: VoidFunction;
};

export const DashboardFilterLinkModeIsland = ({
  linkModeDashboardFilterParameterName,
  exitLinkMode,
  i18nLocale,
}: DashboardFilterLinkModeIslandProps & WithI18nLocale) => (
  <DashboardFilterContainer backgroundColor="surfaceDefault">
    <FlexBox gap="small" flexDirection="column">
      <Header3>
        {t(
          i18nLocale,
          'dashboard:filter.editLinkedCharts',
          'Edit linked charts'
        )}
      </Header3>
      <Text color="textSubtle" variant="text1" truncate={true}>
        {t(
          i18nLocale,
          'dashboard:filter.selectChartsToLinkOrUnlink',
          { name: linkModeDashboardFilterParameterName },
          `Select the charts you want to link to or unlink from {{name}}`
        )}
      </Text>
    </FlexBox>
    <FlexBox gap="small">
      <Button buttonType={ButtonType.PRIMARY} onClick={exitLinkMode}>
        {t(i18nLocale, 'dashboard:filter.done', 'Done')}
      </Button>
    </FlexBox>
  </DashboardFilterContainer>
);
export type DashboardBuilderFilterIslandProps =
  | ({
      isInLinkDashboardFiltersMode: true;
    } & DashboardFilterLinkModeIslandProps)
  | ({
      isInLinkDashboardFiltersMode: false;
    } & DashboardFilterBuilderIslandProps);

export type DashboardFilterBuilderIslandProps = {
  dashboardFilterProps: Array<
    | ({ id: string; isLinkedDashboardFilter: true } & DashboardFilterPillProps)
    | ({
        id: string;
        isLinkedDashboardFilter: false;
      } & UnlinkedDashboardFilterPillProps)
  >;
  showClearAllButton: boolean;
  dashboardFilterNames: string[];
  createDashboardFilterParameter: (name: string, id: string) => void;
  hasInaccessibleWidgetsForDashboardFilter: boolean;
  clearAllSelectedValues: VoidFunction;
  reorderDashboardFilterParameters: (
    fromIndex: number,
    toIndex: number
  ) => void;
};

const DraggablePillWrapper = styled(DragAndDropCardWrapper)<{
  $isBeingDragged: boolean;
}>`
  width: fit-content;
  height: fit-content;
  align-items: center;
  opacity: ${({ $isBeingDragged }) => ($isBeingDragged ? 0.4 : 1)};
  &:hover ${DragAndDropPositioningWrapper} > * {
    border-color: ${colors.borderActionActive};
  }
`;

const DraggablePill = ({
  dragAndDropCardId,
  children,
}: {
  dragAndDropCardId: string;
  children: ReactNode;
}) => {
  const { draggedCardId } = useDragAndDropContext();
  return (
    <DraggablePillWrapper
      dragAndDropCardId={dragAndDropCardId}
      dragButtonAlwaysVisible
      dragButtonPosition="left"
      $isBeingDragged={draggedCardId === dragAndDropCardId}
    >
      {children}
    </DraggablePillWrapper>
  );
};

// End margin absorbs the 3px DropPlaceholderBar border so the bar sits
// symmetrically between pills.
const DashboardFilterFlexBox = styled(FlexBox)`
  & > ${DropPlaceholderBar} {
    margin-inline: -${s8} calc(-${s8} - 3px);
  }
`;

export const DashboardFilterBuilderIsland = ({
  dashboardFilterProps,
  showClearAllButton,
  clearAllSelectedValues,
  reorderDashboardFilterParameters,
  i18nLocale,
  ...createButtonProps
}: DashboardFilterBuilderIslandProps & WithI18nLocale) => {
  const handleDrop = (
    newOrder: string[],
    { draggedCardId }: DragAndDropDragState
  ) => {
    if (!draggedCardId) return;
    const fromIndex = dashboardFilterProps.findIndex(
      ({ id }) => id === draggedCardId
    );
    const toIndex = newOrder.indexOf(draggedCardId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;
    reorderDashboardFilterParameters(fromIndex, toIndex);
  };

  const pills = dashboardFilterProps.map(props =>
    props.isLinkedDashboardFilter ? (
      <DashboardFilterPillView key={props.id} {...props} />
    ) : (
      <UnlinkedDashboardFilterPillView key={props.id} {...props} />
    )
  );

  return (
    <DashboardFilterFlexBox
      width="full"
      align="center"
      gap="medium"
      flexWrap
      maxWidth={`${DASHBOARD_MAX_WIDTH}px`}
    >
      <CreateDashboardFilterButtonAndForm
        {...createButtonProps}
        i18nLocale={i18nLocale}
      />
      <DragAndDropSectionsProvider
        onDrop={handleDrop}
        orientation="horizontal"
        showDropIndicatorDot={false}
      >
        {dashboardFilterProps.map((props, index) => (
          <DraggablePill key={props.id} dragAndDropCardId={props.id}>
            {pills[index]}
          </DraggablePill>
        ))}
      </DragAndDropSectionsProvider>
      {showClearAllButton && (
        <GhostButton
          buttonSize={ButtonSize.SMALL}
          onClick={clearAllSelectedValues}
        >
          {t(i18nLocale, 'dashboard:filter.clearAll', 'Clear all')}
        </GhostButton>
      )}
    </DashboardFilterFlexBox>
  );
};
